import { Component, OnDestroy, OnInit } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AgentEmbedService } from 'shared/services/agent-embed/agent-embed.service';
import { AgentWindowStateService } from 'shared/services/agent-window-state/agent-window-state.service';

// BRN-2118 (AC1): the floating, always-on-top AI-chat launcher -- replaces the (retired) main-menu
// item per lifecycle/decisions/floating-chat-entry-point.md. Availability-gated exactly as the
// menu item would have been (AgentEmbedService.available$); additionally hidden while the panel is
// open, reappearing on close. Mounted once at the app-shell root (app.component.html) so it
// survives SPA route navigation.
const IDLE_PULSE_INTERVAL_MS = 30_000;
const IDLE_PULSE_DURATION_MS = 1_200;

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

@Component({
  selector: 'app-agent-chat-launcher',
  templateUrl: './agent-chat-launcher.component.html',
  styleUrls: ['./agent-chat-launcher.component.scss'],
})
export class AgentChatLauncherComponent implements OnInit, OnDestroy {
  readonly available$: Observable<boolean> = this.agentEmbed.available$;
  readonly isOpen$: Observable<boolean> = this.windowState.state$.pipe(map((state) => state.isOpen));

  idlePulse = false;

  private idlePulseTimer: ReturnType<typeof setInterval> | null = null;
  private pulseResetTimer: ReturnType<typeof setTimeout> | null = null;
  private interacted = false;

  constructor(
    private readonly agentEmbed: AgentEmbedService,
    private readonly windowState: AgentWindowStateService,
  ) {}

  ngOnInit(): void {
    // Occasional single-cycle idle pulse, only while closed, stops for the session after first
    // interaction -- lifecycle/decisions/floating-chat-entry-point.md's micro-animation contract.
    this.idlePulseTimer = setInterval(() => {
      if (this.interacted || prefersReducedMotion()) {
        return;
      }
      this.idlePulse = true;
      this.pulseResetTimer = setTimeout(() => {
        this.idlePulse = false;
      }, IDLE_PULSE_DURATION_MS);
    }, IDLE_PULSE_INTERVAL_MS);
  }

  ngOnDestroy(): void {
    if (this.idlePulseTimer !== null) {
      clearInterval(this.idlePulseTimer);
    }
    if (this.pulseResetTimer !== null) {
      clearTimeout(this.pulseResetTimer);
    }
  }

  open(): void {
    this.interacted = true;
    this.idlePulse = false;
    this.windowState.open();
  }
}
